// src/App.js
// FINAL CLEAN VERSION
import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import { Snackbar, Button } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import CircularProgress from '@mui/material/CircularProgress';
import baseTheme from './theme'; 
import Navbar from './components/Navbar';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { AriaContextProvider } from './contexts/AriaContext'; 
import ProtectedRoute from './components/ProtectedRoute';
import { RegistrationProvider } from './contexts/RegistrationContext';
import { TenantProvider, useTenant } from './contexts/TenantContext'; 
// import ProfileCompletionModal from './components/auth/ProfileCompletionModal';

// --- Lazy-loaded Page Components ---
const NewPatientDashboard = React.lazy(() => import('./pages/NewPatientDashboard'));
const MyMedsWatcherPortal = React.lazy(() => import('./pages/MyMedsWatcherPortal'));
const MyMedsPatientView = React.lazy(() => import('./pages/MyMedsPatientView'));
const AssignmentDetailPage = React.lazy(() => import('./pages/AssignmentDetailPage'));
const ContextSelectionPage = React.lazy(() => import('./pages/ContextSelectionPage'));
const MyHealthRecords = React.lazy(() => import('./components/MyHealthRecords'));
const Chatbot = React.lazy(() => import('./components/Chatbot'));
const LearnPage = React.lazy(() => import('./pages/LearnPage'));
const MeetOurExpertsPage = React.lazy(() => import('./pages/MeetOurExpertsPage'));
const TestimonialsPage = React.lazy(() => import('./pages/TestimonialsPage'));
const MeetDrAmitJain = React.lazy(() => import('./components/MeetDrAmitJain')); 
const RoutineActionHandler = React.lazy(() => import('./components/RoutineActionHandler'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const ActionVerification = React.lazy(() => import('./pages/ActionVerification'));
const GuardianPortal = React.lazy(() => import('./pages/GuardianPortal'));
const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation'));

// [UNIFIED AUTH COMPONENTS]
const EnrollPage = React.lazy(() => import('./pages/EnrollPage')); 
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));

const queryClient = new QueryClient({
    defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

function AppLayout() {
    const { patientEpisodes, user, isLoading } = useAuth();
    const location = useLocation(); 
    return (
        <Box sx={{ display: 'flex' }}>
            <Navbar />
                <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, width: '100%', minHeight: '100dvh', display: 'flex', flexDirection: 'column', pt: 'env(safe-area-inset-top)' }}>
                <Toolbar />
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>}>
                        <Routes>
                            {/* Dashboard Routes */}
                            <Route path="/" element={<ProtectedRoute><MyMedsPatientView /></ProtectedRoute>} />
                            <Route path="/dashboard" element={<ProtectedRoute><NewPatientDashboard episodes={patientEpisodes} /></ProtectedRoute>} />
                            <Route path="/mymeds/dashboard" element={<ProtectedRoute><MyMedsWatcherPortal /></ProtectedRoute>} />
                            <Route path="/mymeds/patient" element={<ProtectedRoute><MyMedsPatientView /></ProtectedRoute>} />
                            
                            {/* App Features */}
                            <Route path="/chatbot" element={<ProtectedRoute><Chatbot /></ProtectedRoute>} />
                            <Route path="/my-records" element={<ProtectedRoute><MyHealthRecords /></ProtectedRoute>} />
                            <Route path="/assignment/:assignmentId" element={<ProtectedRoute><AssignmentDetailPage /></ProtectedRoute>} />
                            <Route path="/select-context" element={<ProtectedRoute><ContextSelectionPage /></ProtectedRoute>} />
                            <Route path="/guardian-portal" element={<ProtectedRoute><GuardianPortal /></ProtectedRoute>} />
                            <Route path="/do/:routineType" element={<ProtectedRoute><RoutineActionHandler /> </ProtectedRoute>} />

                            {/* Public/Marketing */}
                            <Route path="/learn" element={<ProtectedRoute><LearnPage /></ProtectedRoute>} />
                            <Route path="/experts" element={<ProtectedRoute><MeetOurExpertsPage /></ProtectedRoute>} />
                            <Route path="/testimonials" element={<ProtectedRoute><TestimonialsPage /></ProtectedRoute>} />
                            <Route path="/meet-dr-amit-jain" element={<MeetDrAmitJain />} />
                            
                            {/* Auth & Onboarding Flow */}
                            <Route path="/enroll" element={<EnrollPage />} />

                            {/* Redirect Legacy Routes to New Unified Page */}
                            <Route path="/login" element={<Navigate to="/enroll?mode=login" replace />} />
                            <Route path="/register" element={<Navigate to="/enroll?mode=enroll" replace />} />
                            <Route path="/forgot-password" element={<Navigate to="/enroll?mode=forgot" replace />} />

                            {/* Required Support Routes */}
                            <Route path="/reset-password/:token" element={<ResetPassword />} />
                            <Route path="/auth/callback" element={<AuthCallback />} />
                            <Route path="/verify" element={<ActionVerification />} />
                            <Route path="/accept-invitation" element={<AcceptInvitation />} />

                            {/* Legacy cleanup */}
                            <Route path="/join-family" element={<Navigate to="/accept-invitation" replace />} /> 
                        </Routes>
                    </Suspense>
                </Box>
            </Box>
        </Box>
    );
}

function MainApp() {
    const [showReload, setShowReload] = useState(false);
    const [waitingWorker, setWaitingWorker] = useState(null);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => { e.preventDefault(); window.deferredPrompt = e; };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        serviceWorkerRegistration.register({
            onUpdate: registration => { setWaitingWorker(registration.waiting); setShowReload(true); }
        });

        const handleControllerChange = () => {
            if (!window.isRefreshing) { window.isRefreshing = true; window.location.reload(); }
        };
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
        return () => {
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const reloadPage = () => {
        if (waitingWorker) waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        setShowReload(false); 
    };

    return (
        <>
            <AppLayout />
            <Snackbar
                open={showReload} message="A new version is available."
                onClick={reloadPage} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                action={<Button color="inherit" size="small" onClick={reloadPage}>RELOAD</Button>}
            />
        </>
    );
}

const DynamicThemeProvider = ({ children }) => {
    const { selectedTenant } = useTenant();
    const dynamicTheme = React.useMemo(() => {
        const primaryColor = selectedTenant?.primary_theme_color || baseTheme.palette.primary.main;
        const secondaryColor = selectedTenant?.secondary_theme_color || baseTheme.palette.secondary.main;
        return responsiveFontSizes(createTheme({ ...baseTheme, palette: { ...baseTheme.palette, primary: { ...baseTheme.palette.primary, main: primaryColor }, secondary: { ...baseTheme.palette.secondary, main: secondaryColor } } }));
    }, [selectedTenant]);
    return <ThemeProvider theme={dynamicTheme}><CssBaseline />{children}</ThemeProvider>;
};

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <TenantProvider>
                        <RegistrationProvider>
                            <AriaContextProvider>
                                <DynamicThemeProvider>
                                    <MainApp />
                                </DynamicThemeProvider>
                            </AriaContextProvider>
                        </RegistrationProvider>
                    </TenantProvider>
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}
export default App;